import { Injectable } from "@angular/core";
import { ProductType } from "@obg/core.config";
import { isLocalStorageSupported, PlatformService } from "@obg/ngx.global";

import { STL_RC_TOLERANCE_DELAY_IN_MS } from "../responsible-gaming.const";

import { RealityCheckConfigService } from "./reality-check.config";

const STORE_PREFIX = "common.reality-check";
const DISABLED_STORE_KEY = `${STORE_PREFIX}.disabled`;

@Injectable({
	providedIn: "root",
})
export class RealityCheckService {
	constructor(
		private config: RealityCheckConfigService,
		private platformService: PlatformService
	) {}

	getEnabledStatus(): boolean {
		return (
			this.platformService.isBrowser &&
			this.config.getIsRealityCheckEnabled() &&
			!localStorage?.getItem(DISABLED_STORE_KEY)
		);
	}

	disableRealityCheck(): void {
		if (isLocalStorageSupported()) {
			localStorage.setItem(DISABLED_STORE_KEY, "true");
		}
	}

	/**
	 * Calculates the delay based on the current state. If it is bigger than the specified interval, it will return 0.
	 *
	 * @returns The delay in ms if it is within the given interval, otherwise 0.
	 */
	getDelayUntilNextRealityCheck(nextPulse: number | undefined, intervalInMs: number): number {
		if (!nextPulse) {
			return 0;
		}

		const diff = nextPulse - Date.now();
		return diff < intervalInMs ? diff + STL_RC_TOLERANCE_DELAY_IN_MS : 0;
	}

	/**
	 * Returns whether reality check was triggered within the given interval.
	 */
	wasRealityCheckTriggeredWithinInterval(triggeredAt: number, intervalInMs: number): boolean {
		return Date.now() - triggeredAt < intervalInMs;
	}

	/**
	 * Returns whether reality check is active for a given product.
	 */
	isRealityCheckActiveForProduct(product: ProductType | undefined): boolean {
		return !!(product && this.config.getRealityCheckActiveProductMap()[product]);
	}

	/**
	 * Returns whether reality check timer should be started based on product change.
	 */
	shouldTriggerStartForProductChange(
		currentProduct: ProductType | undefined,
		previousProduct: ProductType | undefined
	): boolean {
		return (
			this.isRealityCheckActiveForProduct(currentProduct) && !this.isRealityCheckActiveForProduct(previousProduct)
		);
	}

	/**
	 * Returns whether reality check timer should be stopped based on product change.
	 */
	shouldTriggerStopForProductChange(
		currentProduct: ProductType | undefined,
		previousProduct: ProductType | undefined
	): boolean {
		return (
			!this.isRealityCheckActiveForProduct(currentProduct) && this.isRealityCheckActiveForProduct(previousProduct)
		);
	}
}
